import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import UserIcon from "../../assets/images/icon/profile.png";
import UserService from "../../services/user";
import { updateUserProfile } from "../../store/slices/userSlice";
import { toastError, toastSuccess } from "../Toast";
import { useLanguage } from "../../context/LanguageContext";
import {
  persistProfileToStorage,
  readStoredEmail,
  readStoredImage,
  readStoredName,
} from "../../hooks/useSidebarData";

const ProfileModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const [updating, setUpdating] = useState(false);

  const [email, setEmail] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const formik = useFormik({
    initialValues: { name: "" },
    validationSchema: Yup.object({
      name: Yup.string().trim().required(t.fieldRequired || "Full name is required"),
    }),
    onSubmit: (values) => updateProfile(values.name),
  });

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      return;
    }

    formik.setValues({ name: readStoredName() || "" });
    formik.setTouched({});
    setEmail(readStoredEmail());
    setImagePreview(readStoredImage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";

    const handleEsc = (e) => {
      if (e.key === "Escape" && !updating) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, updating]);

  if (!isOpen) return null;

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const updateProfile = async (name) => {
    setUpdating(true);

    try {
      const formData = new FormData();

      formData.append("name", name);

      // Send firstName for backward compatibility
      formData.append("firstName", name);

      // Send ONLY the actual selected file.
      // Do not send imagePreview because it can be a blob URL.
      if (selectedFile) {
        formData.append("profileImage", selectedFile);
      }

      const res = await UserService.updateProfile(formData);
      const updatedData = res?.data?.data;

      const merged = {
        ...(updatedData || {}),
        name: updatedData?.name || name,
        firstName: updatedData?.firstName || name,
        email: updatedData?.email || email,
      };

      dispatch(updateUserProfile(merged));
      persistProfileToStorage(merged);

      toastSuccess(
        res?.data?.message ||
        t.profileUpdated ||
        "Profile updated successfully!"
      );

      window.dispatchEvent(new Event("profileUpdated"));
      onClose();
    } catch (err) {
      console.error("Error updating profile:", err);

      toastError(
        err?.response?.data?.message ||
        "Failed to update profile"
      );
    } finally {
      setUpdating(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => {
        if (!updating) onClose();
      }}
    >
      {/* Small Compact Centered Popup Modal */}
      <div
        className="relative bg-white rounded-[24px] border border-gray-200 p-5 sm:p-6 w-full max-w-[340px] flex flex-col gap-4 shadow-2xl my-auto flex-shrink-0"
        style={{ maxWidth: "340px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={() => {
            if (!updating) onClose();
          }}
          disabled={updating}
          className="absolute top-3.5 right-3.5 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all disabled:opacity-30 rounded-full hover:bg-gray-100"
          aria-label="Close"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L15 15M15 1L1 15"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Modal Title */}
        <div className="text-center">
          <h2 className="font-roboto font-extrabold text-[17px] uppercase tracking-wide text-gray-800">
            {t.editProfile || "Edit Profile"}
          </h2>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col gap-4"
        >
          {/* Small Avatar Section */}
          <div className="flex flex-col items-center justify-center gap-2 flex-shrink-0">
            <div
              className="relative group cursor-pointer flex-shrink-0"
              style={{ width: "100px", height: "100px" }}
              onClick={() =>
                document
                  .getElementById("small-profile-img-input")
                  ?.click()
              }
            >
              <div
                className="rounded-full border-2 border-[#904DB0] overflow-hidden flex items-center justify-center shadow-md transition-transform group-hover:scale-105 flex-shrink-0"
                style={{
                  width: "100px",
                  height: "100px",
                  minWidth: "100px",
                  minHeight: "100px",
                  maxWidth: "100px",
                  maxHeight: "100px",
                  aspectRatio: "1 / 1",
                  background: "linear-gradient(to top right, #C7BFE5 0%, #904DB0 100%)",
                }}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Profile"
                    className="w-full h-full object-cover rounded-full flex-shrink-0"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <img
                    src={UserIcon}
                    alt="Profile"
                    className="w-12 h-12 object-contain flex-shrink-0"
                  />
                )}
              </div>

              <label
                htmlFor="small-profile-img-input"
                className="absolute bottom-0 right-0 bg-[#904DB0] border-2 border-white rounded-full shadow-md cursor-pointer hover:bg-[#a65cc9] transition-all flex items-center justify-center z-10"
                style={{ width: "32px", height: "32px" }}
                title="Upload Image"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-white"
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle
                    cx="12"
                    cy="13"
                    r="4"
                  />
                </svg>
              </label>

              <input
                type="file"
                id="small-profile-img-input"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>

            {/* Display Full Name below avatar */}
            {formik.values.name && (
              <span className="font-roboto text-[15px] font-bold text-gray-800 leading-none text-center mt-1">
                {formik.values.name}
              </span>
            )}
          </div>

          {/* Full Name */}
          <div className="flex flex-col gap-1.5">
            <label className="font-roboto text-[12px] font-bold text-gray-700 uppercase tracking-wide">
              {t.fullNameLabel || "Full Name"}
              <span className="text-red-500"> *</span>
            </label>

            <input
              type="text"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t.fullNamePlaceholder || "Full name"}
              className={`w-full h-[40px] bg-white rounded-full px-4 text-gray-800 text-[13px] leading-none focus:outline-none font-roboto placeholder:text-gray-400 transition-colors ${
                formik.touched.name && formik.errors.name
                  ? "border-2 border-red-500"
                  : "border border-gray-300 focus:border-purple-500"
              }`}
            />

            {formik.touched.name && formik.errors.name && (
              <div className="text-red-500 text-xs mt-0.5">{formik.errors.name}</div>
            )}
          </div>

          {/* Email Field (Read-only) */}
          <div className="flex flex-col gap-1.5">
            <label className="font-roboto text-[12px] font-bold text-gray-700 uppercase tracking-wide">
              {t.emailLabel || "Email"}
            </label>

            <input
              type="email"
              value={email}
              readOnly
              disabled
              className="w-full h-[40px] bg-gray-50 border border-gray-300 rounded-full px-4 text-gray-700 text-[13px] leading-none focus:outline-none cursor-not-allowed font-roboto"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-3 w-full mt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={updating}
              className="flex-1 h-[34px] px-4 rounded-full border-2 border-gray-800 text-gray-800 font-roboto font-bold text-[12px] uppercase leading-none flex items-center justify-center hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              {t.cancelBtn || "Cancel"}
            </button>

            <button
              type="submit"
              disabled={updating}
              className="flex-1 h-[34px] px-4 rounded-full bg-[#904DB0] hover:bg-[#a65cc9] text-white font-roboto font-bold text-[12px] uppercase leading-none flex items-center justify-center transition-all disabled:opacity-50 shadow-md"
            >
              {updating ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t.saveBtn || "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ProfileModal;